/**
 * Enquiries that arrive as a LINK rather than an attachment.
 *
 * ---- Real, and not rare ----
 *
 * Measured across 60 inbound mails on the bench: 7 carry a link to a file rather than the file
 * itself — mostly one customer's Azure Blob exports, plus a couple of Google Drive shares:
 *
 *     10  cfmoversearsmarinelog.blob.core.windows.net
 *      2  drive.google.com
 *
 * `FW: Request for Quotation of our Inquiry No. 427-070/2026d-I` links to
 * `Inquiry_-_Aqua_Maris_Ship_supply_LLC..pdf`, and until now the mail looked like it had no
 * requisition at all: nothing is attached, so the reader reports nothing to read.
 *
 * ---- Why this finds the link but does not fetch it ----
 *
 * The file IS reachable — the URL carries its own SAS token, and fetching it server-side returns
 * 200 and a real PDF. What it does not carry is `Access-Control-Allow-Origin`, so the browser
 * refuses to read it, exactly as it refuses Frappe's own `/private/files` (see `api/fileUrl.ts`,
 * which documents the same trap).
 *
 * Fetching it through our own server would work and is deliberately NOT done here: that is a proxy
 * which fetches whatever URL an inbound email tells it to, from inside the network — the shape of
 * an SSRF, and the reason `server/allowlist.ts` is default-deny in the first place. Opening that up
 * is a decision with a security answer, not a parsing one.
 *
 * So this surfaces the link where the purchaser is already deciding what to do with the mail. They
 * open it, the browser downloads it, and the upload control beside it reads the file — which needs
 * no endpoint at all (`readUploadedFile.ts`).
 */

/** Extensions worth offering. A link to a website is not an enquiry. */
const FILE_EXTENSIONS = /\.(xlsx?|xlsm|xlsb|pdf|docx?|csv)(\?|$)/i

/**
 * Hosts whose links are file shares even when the URL carries no extension — a Drive or Dropbox
 * link names the file nowhere in its path.
 */
const SHARE_HOSTS = /(^|\.)(drive\.google\.com|docs\.google\.com|dropbox\.com|wetransfer\.com|onedrive\.live\.com|.*\.sharepoint\.com|.*\.blob\.core\.windows\.net)$/i

export interface MailFileLink {
  url: string
  /** The file's name where the URL states one, else the host — something to show on the control. */
  label: string
}

/** Decoded, because a mail body carries `&amp;` and a half-decoded SAS token authorises nothing. */
function decodeEntities(value: string): string {
  return value.replace(/&amp;/g, '&').replace(/&#38;/g, '&')
}

function labelFor(url: string): string {
  try {
    const parsed = new URL(url)
    const last = parsed.pathname.split('/').filter(Boolean).pop()
    if (last && FILE_EXTENSIONS.test(last)) return decodeURIComponent(last)
    return parsed.host
  } catch {
    return url
  }
}

/**
 * Links in a mail body that look like a downloadable enquiry.
 *
 * De-duplicated on the URL: a mail that shows the same link as text and as an anchor would
 * otherwise offer it twice. Order is the body's own, so the first link is the one the customer
 * wrote first.
 */
export function fileLinksIn(bodyHtml: string): MailFileLink[] {
  const seen = new Set<string>()
  const out: MailFileLink[] = []

  for (const match of bodyHtml.matchAll(/https?:\/\/[^\s"'<>)]+/g)) {
    const url = decodeEntities(match[0]).replace(/[.,;]+$/, '')
    if (seen.has(url)) continue

    let host = ''
    try {
      host = new URL(url).host
    } catch {
      continue
    }

    if (!FILE_EXTENSIONS.test(url) && !SHARE_HOSTS.test(host)) continue
    seen.add(url)
    out.push({ url, label: labelFor(url) })
  }

  return out
}
